import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Building } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const ContactUsPage = () => {
  const navigate = useNavigate();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO 
        title="Contact AMP Predict | Support &amp; Feedback"
        description="Contact the AMP Predict team for support, feedback, partnership, or press inquiries. Email ampcontactus@anjolc.com — replies typically within 1–2 business days."
        keywords="contact AMP Predict, ANJO LC contact, prediction market support, customer service, response time"
        url="https://amppredict.com/contact"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#3B82F6]/20 rounded-xl">
            <Mail className="h-6 w-6 text-[#3B82F6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="contact-h1">Get in touch — support, feedback, and press inquiries</h1>
            <p className="text-gray-400 text-sm">The AMP Predict team is here to help</p>
          </div>
        </div>

        {/* Response expectations */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Response times &amp; office hours</h2>
          <p className="text-gray-300 leading-relaxed">
            AMP Predict is operated by ANJO LC out of Maryland, USA. Our support team is online <strong className="text-white">Monday–Friday, 9:00 AM – 6:00 PM Eastern</strong>.
            We reply to most email inquiries within <strong className="text-white">1–2 business days</strong>, and to billing or refund
            requests submitted via the Billing page within <strong className="text-white">3 business days</strong>. Weekend messages
            are queued and answered on the next business day.
          </p>
        </div>

        {/* What to include */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">What to include in your message</h2>
          <p className="text-gray-300 leading-relaxed mb-3">
            To help us respond faster, please include the following when you email us:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-300">
            <li><strong className="text-white">Your account email</strong> — the address you signed up with (so we can locate your account).</li>
            <li><strong className="text-white">Category of request</strong> — support, feedback, bug report, refund, partnership, or press.</li>
            <li><strong className="text-white">A clear description</strong> — for bugs, the steps that reproduce the issue and any screenshots; for refunds, the payment date and reason.</li>
            <li><strong className="text-white">Your device and browser</strong> if the issue is web-specific, or your iOS/Android version if it's on the mobile app.</li>
          </ul>
        </div>

        {/* Common inquiry types */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Common inquiry types</h2>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            <div>
              <h3 className="text-white font-medium mb-1">Billing &amp; refunds</h3>
              <p>Managed from the Billing page in your Profile. Prorated refunds are available within 7 days of the most recent payment — see the <Button variant="link" onClick={() => navigate('/refund-policy')} className="text-[#3B82F6] p-0 h-auto align-baseline">Refund Policy</Button> for full terms.</p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Account &amp; data</h3>
              <p>Delete your account and all associated data at any time from Profile → Settings → Delete Account. We honor deletion requests immediately and remove personally identifiable information from our production database.</p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Bug reports</h3>
              <p>Please describe the exact steps that reproduce the issue, the market or page URL where it occurs, and attach a screenshot or short screen recording if possible. This dramatically speeds up our fix time.</p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Press &amp; partnerships</h3>
              <p>For press inquiries, interview requests, or partnership proposals (educational institutions, forecasting clubs, research groups), email us with "PRESS" or "PARTNERSHIP" in the subject line and we'll route to the right person.</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Contact Information</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#3B82F6]/10 rounded-xl">
                  <Mail className="h-5 w-5 text-[#3B82F6]" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Email</h3>
                  <a 
                    href="mailto:ampcontactus@anjolc.com" 
                    className="text-[#3B82F6] hover:underline"
                  >
                    ampcontactus@anjolc.com
                  </a>
                  <p className="text-gray-500 text-sm mt-1">
                    We typically respond within 24-48 hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#F97316]/10 rounded-xl">
                  <Building className="h-5 w-5 text-[#F97316]" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Company</h3>
                  <p className="text-gray-300">ANJO LC</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <MapPin className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Location</h3>
                  <p className="text-gray-300">Maryland, USA</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Quick Links</h2>
            
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => navigate('/refund-policy')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Refund Policy
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/terms')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Terms of Service
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/privacy')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Privacy Policy
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/profile/billing')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Billing & Subscriptions
              </Button>
            </div>

            <div className="mt-6 p-4 bg-[#1e2a45]/50 rounded-xl">
              <p className="text-gray-400 text-sm">
                For billing inquiries or refund requests, please visit your 
                <Button 
                  variant="link" 
                  onClick={() => navigate('/profile/billing')} 
                  className="text-[#3B82F6] p-0 h-auto mx-1"
                >
                  Billing page
                </Button>
                or email us directly.
              </p>
            </div>
          </div>
        </div>

        {/* Email CTA */}
        <div className="mt-8 text-center">
          <a
            href="mailto:ampcontactus@anjolc.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl font-medium transition-colors"
          >
            <Mail className="h-5 w-5" />
            Send us an Email
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactUsPage;
